const PLUGIN_KEY = 'collapsible';

const CollapsibleEvents = {
    open: 'open.collapsible',
    close: 'close.collapsible',
    toggle: 'toggle.collapsible',
    click: 'click.collapsible',
};

const CollapsibleState = {
    closed: 'closed',
    open: 'open',
};

function prependHash(id) {
    if (id && id.indexOf('#') === 0) {
        return id;
    }
    return `#${id}`;
}

function optionsFromData(element) {
    return {
        disabledBreakpoint: element.dataset[`${PLUGIN_KEY}DisabledBreakpoint`],
        disabledState: element.dataset[`${PLUGIN_KEY}DisabledState`],
        enabledState: element.dataset[`${PLUGIN_KEY}EnabledState`],
        openClassName: element.dataset[`${PLUGIN_KEY}OpenClassName`],
        iconCloseClassName: element.dataset[`${PLUGIN_KEY}IconCloseClassName`],
    };
}

/**
 * Collapse/Expand toggle
 */
class Collapsible {
      /**
     * @param {HTMLElement} $toggle - Trigger button
     * @param {HTMLElement} $target - Content to collapse / expand
     * @param {Object} [options] - Configurable options
     * @param {Object} [options.$context]
     * @param {String} [options.disabledBreakpoint]
     * @param {Object} [options.disabledState]
     * @param {Object} [options.enabledState]
     * @param {String} [options.openClassName]
     * @example
     *
     * <button id="#more">Collapse</button>
     * <div id="content">...</div>
     */
    constructor(toggle, target, {
        disabledBreakpoint,
        disabledState,
        enabledState,
        openClassName = 'is-open',
        iconCloseClassName = 'icon-close',
    } = {}) {
        this.toggle = toggle;
        this.target = target;
        this.targetId = target.id;
        this.openClassName = openClassName;
        this.iconCloseClassName = iconCloseClassName;
        this.disabledState = disabledState;
        this.enabledState = enabledState;
        this.target.classList.add('hidden');

        if (disabledBreakpoint) {
            this.disabledMediaQueryList = window.matchMedia(disabledBreakpoint);
        }

        this.disabled = this.disabledMediaQueryList ? this.disabledMediaQueryList.matches : false;
        // Bind functions to instance
        this.onClicked = this.onClicked.bind(this);
        this.onCloseIconClicked = this.onCloseIconClicked.bind(this); // Add this line
        this.onDisabledMediaQueryListMatch = this.onDisabledMediaQueryListMatch.bind(this);

        // Set attributes
        this.target.setAttribute('aria-hidden', this.isCollapsed);
        this.toggle.setAttribute('aria-label', this._getToggleAriaLabelText(toggle));
        this.toggle.setAttribute('aria-controls', target.id);
        this.toggle.setAttribute('aria-expanded', this.isOpen);

        // Listen to events
        this.bindEvents();
    }

    get isCollapsed() {
        return this.target.classList.contains('hidden') && !this.target.classList.contains(this.openClassName);
    }

    get isOpen() {
        return !this.isCollapsed;
    }

    set disabled(disabled) {
        this._disabled = disabled;
        this.toggleByState(disabled ? this.disabledState : this.enabledState);
    }

    get disabled() {
        return this._disabled;
    }

    _getToggleAriaLabelText(toggle) {
        const childWithText = [...toggle.children].find(child => child.textContent.trim());
        const ariaLabelTarget = childWithText || toggle;
        return ariaLabelTarget.textContent.trim();
    }

    open({ notify = true } = {}) {
        this.toggle.classList.add(this.openClassName);
        this.toggle.setAttribute('aria-expanded', 'true');
        this.target.classList.add(this.openClassName);
        this.target.classList.remove('hidden');
        this.target.setAttribute('aria-hidden', 'false');

        if (notify) {
            this._triggerEvent(this.toggle, CollapsibleEvents.open);
            this._triggerEvent(this.toggle, CollapsibleEvents.toggle);
        }
    }

    close({ notify = true } = {}) {
        this.toggle.classList.remove(this.openClassName);
        this.toggle.setAttribute('aria-expanded', 'false');
        this.target.classList.remove(this.openClassName);
        this.target.setAttribute('aria-hidden', 'true');
        this.target.classList.add('hidden');

        if (notify) {
            this._triggerEvent(this.toggle, CollapsibleEvents.close);
            this._triggerEvent(this.toggle, CollapsibleEvents.toggle);
        }
    }

    toggleState() {
        if (this.isCollapsed) {
            this.open();
        } else {
            this.close();
        }
    }

    toggleByState(state, ...args) {
        if (state === CollapsibleState.open) {
            return this.open(...args);
        } else if (state === CollapsibleState.closed) {
            return this.close(...args);
        }
    }

    bindEvents() {
        this.toggle.addEventListener('click', this.onClicked);
        this.bindCloseIcon();

        if (this.disabledMediaQueryList) {
            this.disabledMediaQueryList.addListener(this.onDisabledMediaQueryListMatch);
        }
    }

    bindCloseIcon() {
        const closeIcon = this.target.querySelector("." + this.iconCloseClassName);

        if (closeIcon) {
            closeIcon.addEventListener('click', this.onCloseIconClicked);
        }
    }

    onCloseIconClicked(event) {
        event.preventDefault();
        this.close();
    }

    unbindEvents() {
        this.toggle.removeEventListener('click', this.onClicked);

        if (this.disabledMediaQueryList) {
            this.disabledMediaQueryList.removeListener(this.onDisabledMediaQueryListMatch);
        }

        const closeIcon = this.target.querySelector(this.iconCloseClassName);
        if (closeIcon) {
            closeIcon.removeEventListener('click', this.onCloseIconClicked);
        }
    }

    onClicked(event) {
        if (this.disabled) return;
        event.preventDefault();
        this.toggleState();
    }

    onDisabledMediaQueryListMatch(media) {
        this.disabled = media.matches;
    }

    _triggerEvent(element, eventName) {
        const event = new CustomEvent(eventName, {
            detail: { collapsible: this }
        });
        element.dispatchEvent(event);
    }
}

/**
 * Convenience method for constructing Collapsible instance
 *
 * @param {string} [selector]
 * @param {Object} [overrideOptions]
 * @param {Object} [overrideOptions.$context]
 * @param {String} [overrideOptions.disabledBreakpoint]
 * @param {Object} [overrideOptions.disabledState]
 * @param {Object} [overrideOptions.enabledState]
 * @param {String} [overrideOptions.openClassName]
 * @return {Array} array of Collapsible instances
 *
 * @example
 * <a href="#content" data-collapsible>Collapse</a>
 * <div id="content">...</div>
 *
 * collapsibleFactory();
 */
function collapsibleFactory(selector = `[data-${PLUGIN_KEY}]`, overrideOptions = {}) {
  const elements = document.querySelectorAll(selector);
  return Array.from(elements).map((toggle) => {
      // To work with href <a> as with id
      const targetId = prependHash(toggle.dataset[PLUGIN_KEY] || toggle.getAttribute('href'));
      const target = document.querySelector(targetId);
      const options = Object.assign(optionsFromData(toggle), overrideOptions);

      return new Collapsible(toggle, target, options);
  });
}

document.addEventListener('DOMContentLoaded', function () {
    collapsibleFactory();
});

/**
 * Usage examples:
 * By default collapsible state is hidden
 * By default icon close class name is icon-close (not required)
 * 
 * <a id="toggleButton" href="content">Toggle Content</a>
 * <div id="content" class="hidden">This is collapsible content.</div>
 * That is a simple example of usage with collapsed state by default
 * 
 *  <button id="toggleButton"
 *      data-collapsible="#content"
 *      data-collapsible-disabled-breakpoint="(max-width: 600px)"
 *      data-collapsible-disabled-state="open"
 *      data-collapsible-enabled-state="closed"
 *      data-collapsible-open-class-name="expanded"
 *  >
 *     Toggle Sidebar
 *   </button>
 *   <div id="content">This is collapsible content.</div>
 *
 *   The data-collapsible-disabled-breakpoint="(max-width: 600px)" makes the collapsible disabled on screens larger than 600px.
 *   The data-collapsible-open-class-name="expanded" adds the class expanded to both the toggle button and the target content when the content is opened.
 *   The data-collapsible-disabled-state="open" means that when disabled, the collapsible will be forced to the open state.
 *   The data-collapsible-enabled-state="closed" means that when enabled, the collapsible will default to the closed state.
 *
 *   Every attribute could be used separately
 */